/**
 * 대시보드 — 운영 데이터 연동.
 *
 * GET /dashboard/summary 한 번 호출로 5개 영역 데이터 모두 수신:
 *   - metrics             메트릭 4개 (이번달 매출/이익/진행중/발행대기)
 *   - stages              진행단계 분포
 *   - monthlyComparison   12개월 매출 비교 (올해 vs 작년)
 *   - upcomingDeliveries  납품 임박 (today ~ +14일)
 *   - topStock            주요재고 상위 5건
 *
 * 권한:
 *   - viewer: 금액 컬럼 마스킹 + 발행대기 메트릭 hidden
 *   - sales/admin: 모든 카드 표시
 */

import { todayKst } from '../lib/date-label';
import { lazy, Suspense, type ReactNode, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { LastUpdateBadge } from '../components/LastUpdateBadge';
import { DailyChangesSection } from '../components/DailyChangesSection';
import { DrillNum } from '../components/DrillNum';
import type { BreakdownKind } from '../lib/stock-api';
import * as dashboardApi from '../lib/dashboard-api';
import * as stockReleaseApi from '../lib/stock-release-api';
import * as approvalApi from '../lib/approval-api';
// 딜등록 만료 임박 (v2.142.0) — 딜등록 화면과 같은 표시 규칙(lib/deal-display)을 쓴다
import * as dealApi from '../lib/deal-registration-api';
import { ddayText, dealCompanyLabel, dealEndUser, projectTitle } from '../lib/deal-display';
import { LoadingLines } from '../components/ui/Skeleton';
import { TodaySection } from '../components/TodaySection';

/**
 * 재고 숫자 클릭 → 그 숫자를 이루는 건들 (v2.182.0).
 * ⭐**재고관리 화면과 같은 부품**이다 — 대시보드에서 본 숫자와 재고관리에서 본 숫자가
 *   다른 이야기를 하면 안 된다. 첫 화면 무게를 늘리지 않도록 **누를 때 처음 내려받는다.**
 */
const StockBreakdownModal = lazy(() =>
  import('../components/StockBreakdownModal').then((m) => ({ default: m.StockBreakdownModal })),
);
const SalesSummaryModal = lazy(() =>
  import('../components/SalesSummaryModal').then((m) => ({ default: m.SalesSummaryModal })),
);

type AlertKey =
  | 'myApproval' | 'lowStock' | 'upcoming' | 'pendingRelease' | 'pendingInvoice'
  /** 딜등록 연장 마감 임박 (v2.142.0) */
  | 'dealExpiring'
  /** 이번달 매출·이익 카드 클릭 → 구성 내역 (v2.78.0) */
  | 'monthly';

const ROLE_LABEL: Record<string, string> = {
  admin: '관리자',
  mgmt: '경영지원',
  sales: '영업부',
  viewer: '뷰어',
};

const STAGE_CLS: Record<string, string> = {
  일반: 'stage-quote',
  유력: 'stage-likely',
  발주: 'stage-order',
  납품: 'stage-deliv',
  발행: 'stage-issue',
};

/**
 * 대시보드 금액 표기 — **전체 숫자 그대로** (2026-08-24 사용자 요청).
 *
 * 종전에는 「1.23억」·「4,500만」처럼 줄여 썼는데, 매출현황 화면은 전체 숫자를 쓰기 때문에
 * 같은 금액이 화면마다 다르게 보였다. 이제 어디서나 `123,456,789` 한 가지다.
 * ⚠줄임말로 되돌리지 말 것 — 자릿수를 눈으로 세는 것이 사용자 업무 방식이다.
 */
function fmtKrw(n: number | null | undefined): string {
  if (n == null) return '-';
  return Math.round(n).toLocaleString('ko-KR');
}
function fmtCount(n: number): string {
  return `${n.toLocaleString('ko-KR')}건`;
}

export function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const showCost = user?.role === 'admin' || user?.role === 'mgmt' || user?.role === 'sales';
  // 일일 변동 현황 — 열람권한 admin/mgmt (영업지원 요청, 백엔드도 동일 권한 차단)
  const showDailyChanges = user?.role === 'admin' || user?.role === 'mgmt';
  // 내 결재 대기 — 판보 결재는 sales+ (viewer 제외)
  const showApproval = user?.role !== 'viewer';

  const today = new Date();
  const todayKr = today.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'long',
  });
  const todayISO = todayKst(today);

  const summaryQ = useQuery({
    queryKey: ['dashboard', 'summary'],
    queryFn: () => dashboardApi.getSummary(),
    staleTime: 30_000,
  });

  // 출고등록 미승인 건수 — 알림 칩
  const pendingReleaseQ = useQuery({
    queryKey: ['stock-releases', 'pending-count'],
    queryFn: () => stockReleaseApi.getPendingCount(),
    staleTime: 30_000,
  });
  const pendingReleaseCount = pendingReleaseQ.data?.pending ?? 0;

  // 내가 지금 승인할 차례인 판매보고서 (mineCount) — 칩 배지 + 팝업 목록
  const myApprovalQ = useQuery({
    queryKey: ['approvals', 'my-turn', 'dashboard'],
    queryFn: () => approvalApi.listReportApprovals({ mine: true, notFinal: true }),
    enabled: showApproval,
    staleTime: 30_000,
  });
  const myApprovalCount = myApprovalQ.data?.mineCount ?? 0;
  const myApprovalRows = myApprovalQ.data?.rows ?? [];

  // 알림 클릭 → 해당 내용 팝업 (바로 이동하지 않고, 팝업에서 [이동] 버튼으로 이동)
  const [activeAlert, setActiveAlert] = useState<AlertKey | null>(null);

  // 재고표 숫자 클릭 → 그 숫자를 이루는 건들 (v2.182.0 · 재고관리와 같은 창)
  const [drill, setDrill] = useState<{ itemCode: string; kind: BreakdownKind } | null>(null);
  // 그 창 안의 매출번호 클릭 → 영업건 요약 (재고관리와 같은 흐름)
  const [drillSalesId, setDrillSalesId] = useState<string | null>(null);

  // 팝업 열렸을 때만 상세 목록 로드
  const pendingReleaseListQ = useQuery({
    queryKey: ['stock-releases', 'pending-list'],
    queryFn: () => stockReleaseApi.listRecent(),
    enabled: activeAlert === 'pendingRelease',
    staleTime: 30_000,
  });
  const pendingInvoiceListQ = useQuery({
    queryKey: ['dashboard', 'pending-invoices'],
    queryFn: () => dashboardApi.getPendingInvoices(),
    enabled: activeAlert === 'pendingInvoice',
    staleTime: 30_000,
  });
  const pendingReleases = (pendingReleaseListQ.data ?? []).filter((r) => r.status === '등록');
  const lowStockItems = (summaryQ.data?.topStock ?? []).filter((s) => s.isLowStock);
  const upcoming = summaryQ.data?.upcomingDeliveries ?? [];

  /**
   * 딜등록 만료 임박 (v2.142.0 · 사용자 요청 「영업담당자와 영업지원팀에게」).
   * 범위는 서버가 정한다 — 영업지원팀·관리자는 전체, 영업담당자는 내 딜. viewer 는 부르지 않는다(서버도 403).
   * eligible 이 false(담당 딜이 하나도 없는 사람)면 알림도 칸도 안 그린다.
   */
  const dealExpiringQ = useQuery({
    queryKey: ['deals', 'expiring'],
    queryFn: () => dealApi.listExpiringDeals(),
    enabled: !!user && user.role !== 'viewer',
    staleTime: 60_000,
  });
  const dealExpiring = dealExpiringQ.data?.rows ?? [];
  const showDealExpiring = !!dealExpiringQ.data?.eligible;
  const dealScope = dealExpiringQ.data?.scope ?? 'none';
  const dealDays = dealExpiringQ.data?.days ?? 30;

  return (
    <div className="p-4 md:p-8">
      {/* 브랜드 헤더 */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-blue-900 text-white rounded shadow-md mb-6 px-4 md:px-6 py-4 md:py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3 md:gap-5">
          <img src="/logo_white.png" alt="SM SYSTEMS" className="h-8 md:h-10 w-auto" />
          <div>
            <div className="text-[11px] md:text-[11px] uppercase tracking-widest text-blue-200">SMSYSTEMS PORTAL</div>
            <div className="text-base md:text-xl font-bold mt-0.5">에스엠시스템즈 SM 포털</div>
            <div className="text-xs md:text-xs text-slate-400 mt-1 hidden sm:block">스토리지 · 서버 · 나라장터 MAS 통합 운영 시스템</div>
          </div>
        </div>
        <div className="text-left sm:text-right text-xs md:text-xs text-slate-400 leading-relaxed">
          <div>{todayKr}</div>
          <div className="mt-1">
            로그인:{' '}
            <span className="text-white font-semibold">
              {user?.name ?? user?.userId ?? '—'}
            </span>
            {user?.role && <> · {ROLE_LABEL[user.role] ?? user.role}</>}
          </div>
          {/* 기술지원포털 통로 (v2.135.0) — 별도 시스템·별도 로그인이라 새 창으로 연다.
              사이드바 맨 아래에도 같은 링크가 있다. 주소(서버 TSP_PORTAL_URL)가 있을 때만 보인다. */}
          {user?.tspPortalUrl && (
            <a
              href={user.tspPortalUrl}
              target="_blank"
              rel="noopener noreferrer"
              title="기술지원포털 — 별도 시스템 · 별도 로그인 (새 창)"
              className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 rounded border border-white/30 bg-white/10 hover:bg-white/20 text-white text-xs md:text-sm font-semibold"
            >
              🛠 기술지원 포털 ↗
            </a>
          )}
        </div>
      </div>

      {/* 페이지 헤더 */}
      <div className="mb-5">
        <h1 className="ui-page-title">대시보드</h1>
        <p className="ui-page-sub">오늘 {todayISO}</p>
        <LastUpdateBadge className="mt-1" />
      </div>

      {/* v2.319.0 「오늘」 — 내 할 일 · 오늘 납품예정 · 주의 필요 · 내 승인 대기 (Salesforce 홈 Assistant · 기능·메뉴 검토 B1) */}
      <TodaySection />

      {summaryQ.isLoading && (
        <div className="bg-white rounded border border-slate-200 p-12 text-center text-slate-400"><LoadingLines /></div>
      )}
      {summaryQ.isError && (
        <div className="bg-red-50 border border-red-200 rounded p-6 text-center text-red-700">
          대시보드 불러오기 실패: {(summaryQ.error as Error).message}
        </div>
      )}

      {summaryQ.data && (
        <>
          {/* 알림 — 클릭 시 내용 팝업 (팝업에서 [이동] 버튼으로 해당 메뉴 이동) */}
          <div className="bg-white rounded shadow-sm border border-slate-200 p-5 mb-6">
            <div className="text-xs text-slate-500 mb-2">알림 <span className="text-slate-400">(클릭하면 상세 내용)</span></div>
            <div className="flex flex-wrap gap-x-2 gap-y-2 text-sm">
              {showApproval && (
                <AlertItem
                  color="bg-violet-500"
                  label="내 결재 대기"
                  count={`${myApprovalCount}건`}
                  onClick={() => setActiveAlert('myApproval')}
                  title="지금 내가 승인할 차례인 판매보고서"
                />
              )}
              <AlertItem
                color="bg-red-500"
                label="재고 부족"
                count={`${lowStockItems.length}종`}
                onClick={() => setActiveAlert('lowStock')}
                title="부족 품목 보기"
              />
              <AlertItem
                color="bg-amber-400"
                label="납품 임박"
                count={`${upcoming.length}건`}
                onClick={() => setActiveAlert('upcoming')}
                title="납품 임박 건 보기"
              />
              {showDealExpiring && (
                <AlertItem
                  color="bg-orange-500"
                  label="딜등록 만료 임박"
                  count={`${dealExpiring.length}건`}
                  onClick={() => setActiveAlert('dealExpiring')}
                  title={`연장 마감이 ${dealDays}일 안이거나 지난 딜등록 (${dealScope === 'all' ? '전체' : '내 담당'})`}
                />
              )}
              <AlertItem
                color="bg-emerald-500"
                label="출고등록 미승인"
                count={`${pendingReleaseCount}건`}
                onClick={() => setActiveAlert('pendingRelease')}
                title="미승인 출고 보기"
              />
              {summaryQ.data.metrics.pendingInvoice && (
                <AlertItem
                  color="bg-blue-500"
                  label="계산서 미발행"
                  count={`${summaryQ.data.metrics.pendingInvoice.count}건`}
                  onClick={() => setActiveAlert('pendingInvoice')}
                  title="계산서 미발행 건 보기"
                />
              )}
            </div>
          </div>

          {/* 영업진행 주요현황 */}
          <h2 className="ui-modal-title mt-6 md:mt-8 mb-3">영업진행 주요현황</h2>
          <MetricCards
            data={summaryQ.data.metrics}
            showCost={showCost}
            onCardClick={(key) => {
              if (key === 'active') navigate('/sales');
              else if (key === 'pending') setActiveAlert('pendingInvoice');
              else setActiveAlert('monthly');
            }}
          />

          {/* 진행단계별 분포 — 5단계 (일반/유력/발주/납품/발행) */}
          <div className="bg-white rounded shadow-sm border border-slate-200 p-4 md:p-5 mb-6">
            <div className="text-sm font-semibold mb-3">진행단계별 분포</div>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 md:gap-3">
              {summaryQ.data.stages.map((s) => (
                <div
                  key={s.name}
                  className={`text-center p-2 md:p-3 rounded ${STAGE_CLS[s.name] ?? 'bg-slate-100'}`}
                >
                  <div className="text-xs">{s.name}</div>
                  <div className="text-lg md:text-xl font-bold num">{s.count}</div>
                  <div className="text-xs md:text-xs num">
                    {s.totalAmount === null ? '' : fmtKrw(s.totalAmount)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 월별 매출 차트 + 납품 임박 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <MonthlyChart data={summaryQ.data.monthlyComparison} />
            <UpcomingPanel data={summaryQ.data.upcomingDeliveries} />
          </div>

          {/* ⏰ 딜등록 만료 임박 (v2.142.0) — 영업담당자는 내 딜, 영업지원팀·관리자는 전체. 줄을 누르면 그 딜의 상세로 */}
          {showDealExpiring && (
            <DealExpiringPanel
              data={dealExpiring}
              scope={dealScope}
              days={dealDays}
              onOpen={(id) => navigate(`/deal-registration?deal=${id}`)}
              onGoAll={() => navigate('/deal-registration?expiring=1')}
            />
          )}

          {/* 📅 회사 일정 달력은 v2.139.0 에서 **왼쪽 메뉴 [📅 일정]**(/calendar)로 옮겼다.
              사용자 지시(2026-09-08): 「대시보드 내부에 말고 메뉴 중 하나로 제대로 넣어 줘. 입력 편집도
              할 수 있게.」 대시보드에는 종전대로 「납품 임박」만 남는다 — 달력을 여기에도 두면 같은 자료가
              두 곳에 보이고 이 화면이 세로로 길어진다. */}

          {/* 재고 주요현황 — v2.182.0 부터 재고관리와 **같은 수량 칸**을 보여 준다
              (사용자 요청 2026-09-16 「납품예정, 입고예정 등 재고관리에 표현되는 내용 모두」).
              ⚠좁은 화면에서는 재고관리와 똑같이 새 네 칸을 숨긴다 — 폰에서는 품목 이름이 먼저다. */}
          <h2 className="ui-modal-title mt-6 md:mt-8 mb-3">재고 주요현황 (상위 5)</h2>
          {/* 칸 이름만으로는 무슨 수량인지 모른다 — 재고관리 화면과 같은 설명을 여기에도 둔다 */}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 mb-2">
            <span className="hidden md:inline">
              <span className="text-blue-600 font-semibold">납품예정</span> 영업건 연동
            </span>
            <span className="hidden md:inline">
              <span className="text-emerald-600 font-semibold">입고예정</span> 발주 후 미입고
            </span>
            <span className="hidden md:inline">숫자를 누르면 그 수량을 이루는 건들이 열립니다</span>
          </div>
          <div className="bg-white rounded shadow-sm border border-slate-200 overflow-hidden">
            <table className="data-table w-full text-[13px]">
              <thead>
                <tr>
                  <th className="text-left !bg-slate-700 !text-white">품목</th>
                  <th className="text-right !bg-slate-700 !text-white">현재고</th>
                  <th className="text-right !bg-slate-700 !text-white hidden md:table-cell">납품예정</th>
                  <th className="text-right !bg-slate-700 !text-white hidden md:table-cell">입고예정</th>
                  <th className="text-right !bg-slate-700 !text-white hidden md:table-cell">임대</th>
                  <th
                    className="text-right !bg-slate-700 !text-white hidden md:table-cell cursor-help"
                    title="가용재고 = 지금 바로 출고할 수 있는 수량
영업건에 연결(예약)되지 않고 입고 완료된 물량만 셉니다.
아직 안 들어온 발주분(입고예정)은 포함하지 않습니다."
                  >
                    가용 <span className="text-[11px] text-slate-400">ⓘ</span>
                  </th>
                  <th className="text-right !bg-slate-700 !text-white hidden md:table-cell">안전재고</th>
                  <th className="text-center !bg-slate-700 !text-white">상태</th>
                </tr>
              </thead>
              <tbody>
                {summaryQ.data.topStock.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center text-slate-400 py-6">
                      주요재고 데이터가 없습니다
                    </td>
                  </tr>
                )}
                {summaryQ.data.topStock.map((s) => (
                  <tr key={s.itemCode} className={s.isLowStock ? 'bg-red-50' : ''}>
                    <td>
                      <strong>{s.itemName ?? s.itemCode}</strong>{' '}
                      <span className="text-slate-500 text-xs font-mono hidden md:inline">{s.itemCode}</span>
                    </td>
                    <td className="text-right num">{s.currentStock}</td>
                    <td className="text-right num text-blue-600 hidden md:table-cell">
                      <DrillNum
                        value={s.pendingDelivery}
                        onClick={() => setDrill({ itemCode: s.itemCode, kind: 'pending' })}
                        title="클릭 → 납품예정인 건 보기"
                      />
                    </td>
                    <td className="text-right num text-emerald-600 hidden md:table-cell">
                      <DrillNum
                        value={s.incoming}
                        onClick={() => setDrill({ itemCode: s.itemCode, kind: 'incoming' })}
                        title="클릭 → 들어올 발주 건 보기"
                      />
                    </td>
                    <td className="text-right num text-slate-400 hidden md:table-cell">
                      <DrillNum
                        value={s.leased}
                        onClick={() => setDrill({ itemCode: s.itemCode, kind: 'leased' })}
                        title="클릭 → 어디에 임대 중인지 보기"
                      />
                    </td>
                    <td className="text-right num font-semibold hidden md:table-cell">
                      <DrillNum
                        value={s.availableStock}
                        onClick={() => setDrill({ itemCode: s.itemCode, kind: 'available' })}
                        title="클릭 → 지금 출고 가능한 물량이 어느 매입분인지 보기"
                      />
                    </td>
                    <td className="text-right num text-slate-500 hidden md:table-cell">{s.safeStock ?? '-'}</td>
                    <td className="text-center">
                      {s.isLowStock ? (
                        <span className="text-red-600 text-xs font-bold">⚠ 부족</span>
                      ) : (
                        <span className="text-emerald-600 text-xs">정상</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* 일일 변동 현황 (전일 대비) — admin/mgmt 전용 검증 도구.
              지표 사이에 끼어 화면을 밀어내던 것을 최하단 + 기본 접힘으로 (2026-07-19 메뉴 재구성). */}
          {showDailyChanges && (
            <details className="mt-6 md:mt-8">
              <summary className="text-base md:text-lg font-bold cursor-pointer select-none">
                📋 일일 변동 현황 <span className="font-normal text-xs text-slate-500">전일 대비 데이터 검증 (관리자용) · 펼치기</span>
              </summary>
              <div className="mt-3">
                <DailyChangesSection />
              </div>
            </details>
          )}

          {/* 이번달 매출·이익 구성 (v2.78.0) — 큰 숫자 카드 클릭.
              ⭐숫자는 카드와 **같은 질의**에서 온 값이라 합계가 어긋날 수 없다. */}
          {activeAlert === 'monthly' && summaryQ.data.metrics.monthlyParts && (
            <AlertDetailModal
              title={`이번달 매출·이익 구성 (${new Date().getMonth() + 1}월)`}
              color="bg-emerald-500"
              count={fmtKrw(summaryQ.data.metrics.monthlyRevenue)}
              onClose={() => setActiveAlert(null)}
              goLabel="매출 분석으로 이동 ›"
              onGo={() => navigate('/analysis')}
            >
              <table className="w-full text-[13px] border-collapse">
                <thead className="bg-slate-100 text-slate-700">
                  <tr>
                    <th className="text-left px-2 py-1.5">갈래</th>
                    <th className="text-right px-2 py-1.5">매출</th>
                    <th className="text-right px-2 py-1.5">원가</th>
                    <th className="text-right px-2 py-1.5">이익</th>
                  </tr>
                </thead>
                <tbody>
                  {summaryQ.data.metrics.monthlyParts.map((p) => (
                    <tr key={p.key} className="border-t border-slate-100">
                      <td className="px-2 py-1.5 text-slate-700">{p.label}</td>
                      <td className="px-2 py-1.5 text-right num">{fmtKrw(p.revenue)}</td>
                      <td className="px-2 py-1.5 text-right num text-slate-500">{fmtKrw(p.cost)}</td>
                      <td className="px-2 py-1.5 text-right num font-semibold">
                        {fmtKrw(p.revenue - p.cost)}
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
                    <td className="px-2 py-1.5">합계</td>
                    <td className="px-2 py-1.5 text-right num">
                      {fmtKrw(summaryQ.data.metrics.monthlyRevenue)}
                    </td>
                    <td className="px-2 py-1.5 text-right num text-slate-600">
                      {fmtKrw(
                        (summaryQ.data.metrics.monthlyRevenue ?? 0) -
                          (summaryQ.data.metrics.monthlyProfit ?? 0),
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right num">
                      {fmtKrw(summaryQ.data.metrics.monthlyProfit)}
                    </td>
                  </tr>
                </tbody>
              </table>
              <p className="text-xs text-slate-500 mt-2 leading-snug">
                ※ 계산서 발행일이 이번 달인 것만 셉니다 (VAT 별도). 영업건 원가에는 기타비용이
                함께 들어갑니다. 비상벨은 분납서 금액을 VAT 별도로 환산한 값입니다.
              </p>
            </AlertDetailModal>
          )}

          {/* ── 알림 상세 팝업 ───────────────────────────── */}
          {activeAlert === 'myApproval' && (
            <AlertDetailModal
              title="내 결재 대기 (지금 내 차례)"
              color="bg-violet-500"
              count={`${myApprovalCount}건`}
              onClose={() => setActiveAlert(null)}
              goLabel="내 결재함으로 이동 ›"
              onGo={() => navigate('/approvals?tab=report&mine=1')}
            >
              {myApprovalQ.isLoading ? (
                <EmptyNote text="불러오는 중…" />
              ) : myApprovalRows.length === 0 ? (
                <EmptyNote text="지금 승인할 차례인 판매보고서가 없습니다." />
              ) : (
                <ul className="space-y-2 text-sm">
                  {myApprovalRows.map((r) => (
                    <li key={r.salesId} className="p-2 bg-violet-50 rounded">
                      <div className="flex justify-between gap-2">
                        <span className="min-w-0 flex-1">
                          <span className="text-[11px] px-1.5 py-0.5 rounded bg-violet-200 text-violet-800 mr-1 font-semibold">
                            {r.status}
                          </span>
                          <strong className="font-mono text-xs">{r.salesId}</strong>
                          {r.memberCount > 0 && (
                            <span className="ml-1 text-[11px] px-1 rounded border border-indigo-200 bg-indigo-50 text-indigo-700">
                              묶음 +{r.memberCount}
                            </span>
                          )}{' '}
                          <span className="text-slate-700">{r.projectName ?? '-'}</span>
                        </span>
                        {showCost && (
                          <span className="text-slate-600 whitespace-nowrap num">{fmtKrw(r.proposalPrice)}</span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {r.proposalCompany ?? '-'}
                        {r.endUser && ` · ${r.endUser}`}
                        {r.empName && ` · 담당 ${r.empName}`}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </AlertDetailModal>
          )}

          {activeAlert === 'lowStock' && (
            <AlertDetailModal
              title="재고 부족"
              color="bg-red-500"
              count={`${lowStockItems.length}종`}
              onClose={() => setActiveAlert(null)}
              goLabel="재고관리로 이동 ›"
              onGo={() => navigate('/stock')}
            >
              {lowStockItems.length === 0 ? (
                <EmptyNote text="현재 안전재고 미만인 주요재고가 없습니다." />
              ) : (
                <table className="w-full text-[13px] border-collapse">
                  <thead className="bg-slate-100 text-slate-700 sticky top-0 z-10">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold">품목</th>
                      <th className="text-right px-3 py-2 font-semibold">현재고</th>
                      <th className="text-right px-3 py-2 font-semibold">안전재고</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lowStockItems.map((s) => (
                      <tr key={s.itemCode} className="border-b border-slate-100">
                        <td className="px-3 py-2">
                          <strong>{s.itemName ?? s.itemCode}</strong>{' '}
                          <span className="text-slate-500 text-xs font-mono">{s.itemCode}</span>
                        </td>
                        <td className="px-3 py-2 text-right num text-red-600 font-semibold">{s.currentStock}</td>
                        <td className="px-3 py-2 text-right num text-slate-500">{s.safeStock ?? '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </AlertDetailModal>
          )}

          {activeAlert === 'dealExpiring' && (
            <AlertDetailModal
              title={`딜등록 만료 임박 (연장 마감 ${dealDays}일 이내 · ${dealScope === 'all' ? '전체' : '내 담당'})`}
              color="bg-orange-500"
              count={`${dealExpiring.length}건`}
              onClose={() => setActiveAlert(null)}
              goLabel="딜등록 요청으로 이동 ›"
              onGo={() => navigate('/deal-registration?expiring=1')}
            >
              {dealExpiring.length === 0 ? (
                <EmptyNote text={`${dealDays}일 이내 연장 마감인 딜등록이 없습니다.`} />
              ) : (
                <ul className="space-y-2 text-sm">
                  {dealExpiring.map((d) => {
                    const dd = ddayText(d);
                    return (
                      <li key={d.id} className="p-2 bg-orange-50 rounded">
                        <button
                          type="button"
                          onClick={() => navigate(`/deal-registration?deal=${d.id}`)}
                          className="w-full text-left flex justify-between gap-2"
                          title="이 딜의 상세 열기"
                        >
                          <span className="min-w-0 flex-1">
                            <strong className="font-mono text-xs">{d.projectId || d.registrationNo || `#${d.id}`}</strong>{' '}
                            <span className="text-slate-700">
                              {dealCompanyLabel(d)}{dealEndUser(d) ? ` · ${dealEndUser(d)}` : ''}
                            </span>
                            <span className="block text-xs text-slate-500 truncate">{projectTitle(d.projectInfo)}{d.ownerName ? ` · ${d.ownerName}` : ''}</span>
                          </span>
                          <span className="text-orange-700 whitespace-nowrap ml-2 text-right">
                            {d.deadlineDate}
                            {dd ? <span className={`ml-1 px-1.5 rounded text-[11px] font-semibold ${dd.cls}`}>{dd.text}</span> : null}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </AlertDetailModal>
          )}

          {activeAlert === 'upcoming' && (
            <AlertDetailModal
              title="납품 임박 (D+14 이내)"
              color="bg-amber-400"
              count={`${upcoming.length}건`}
              onClose={() => setActiveAlert(null)}
              goLabel="영업진행으로 이동 ›"
              onGo={() => navigate('/sales')}
            >
              {upcoming.length === 0 ? (
                <EmptyNote text="14일 이내 납품 예정 건이 없습니다." />
              ) : (
                <ul className="space-y-2 text-sm">
                  {upcoming.map((d) => (
                    <li key={d.salesId} className="flex justify-between p-2 bg-amber-50 rounded">
                      <span className="min-w-0 flex-1">
                        <strong>{d.salesId}</strong>{' '}
                        <span className="text-slate-700">
                          {d.endUser ?? ''}
                          {d.projectName && ` · ${d.projectName}`}
                        </span>
                      </span>
                      <span className="text-amber-700 whitespace-nowrap ml-2">
                        {d.deliveryDate}
                        <span className="ml-1 text-[11px]">
                          (D{d.daysLeft >= 0 ? '-' : '+'}
                          {Math.abs(d.daysLeft)})
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </AlertDetailModal>
          )}

          {activeAlert === 'pendingRelease' && (
            <AlertDetailModal
              title="출고등록 미승인"
              color="bg-emerald-500"
              count={`${pendingReleaseCount}건`}
              onClose={() => setActiveAlert(null)}
              goLabel="출고검수 열기 ›"
              onGo={() => navigate('/stock?review=1')}
            >
              {pendingReleaseListQ.isLoading ? (
                <EmptyNote text="불러오는 중…" />
              ) : pendingReleases.length === 0 ? (
                <EmptyNote text="승인 대기 중인 출고등록이 없습니다." />
              ) : (
                <ul className="space-y-2 text-sm">
                  {pendingReleases.map((r) => (
                    <li key={r.releaseId} className="p-2 bg-emerald-50 rounded">
                      <div className="flex justify-between gap-2">
                        <span className="font-semibold">
                          <span className="text-[11px] px-1.5 py-0.5 rounded bg-emerald-200 text-emerald-800 mr-1">
                            {r.releaseType}
                          </span>
                          {r.useForLabel}
                        </span>
                        <span className="text-slate-500 whitespace-nowrap">{r.releaseDate}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        등록: {r.requesterName ?? r.requesterUserId} ·{' '}
                        {r.items.map((it) => `${it.itemName ?? it.itemCode ?? '-'}(${it.qty})`).join(', ')}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </AlertDetailModal>
          )}

          {activeAlert === 'pendingInvoice' && (
            <AlertDetailModal
              title="계산서 미발행"
              color="bg-blue-500"
              count={
                summaryQ.data.metrics.pendingInvoice
                  ? `${summaryQ.data.metrics.pendingInvoice.count}건`
                  : undefined
              }
              onClose={() => setActiveAlert(null)}
              goLabel="영업진행으로 이동 ›"
              onGo={() => navigate('/sales')}
            >
              {pendingInvoiceListQ.isLoading ? (
                <EmptyNote text="불러오는 중…" />
              ) : (pendingInvoiceListQ.data ?? []).length === 0 ? (
                <EmptyNote text="계산서 미발행 건이 없습니다." />
              ) : (
                <table className="w-full text-[13px] border-collapse">
                  <thead className="bg-slate-100 text-slate-700 sticky top-0 z-10">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold">매출번호</th>
                      <th className="text-left px-3 py-2 font-semibold">엔드유저 / 건명</th>
                      <th className="text-center px-3 py-2 font-semibold">단계</th>
                      {showCost && <th className="text-right px-3 py-2 font-semibold">제안가</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {(pendingInvoiceListQ.data ?? []).map((p) => (
                      <tr key={p.salesId} className="border-b border-slate-100">
                        <td className="px-3 py-2 font-mono text-xs">{p.salesId}</td>
                        <td className="px-3 py-2">
                          <div>{p.endUser ?? '-'}</div>
                          {p.projectName && (
                            <div className="text-xs text-slate-500 truncate max-w-[260px]">{p.projectName}</div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className={`stage-badge ${STAGE_CLS[p.status] ?? ''} text-[11px]`}>{p.status}</span>
                        </td>
                        {showCost && (
                          <td className="px-3 py-2 text-right num">{p.amount === null ? '-' : fmtKrw(p.amount)}</td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </AlertDetailModal>
          )}
        </>
      )}

      {/* 재고 숫자 클릭 → 그 숫자를 이루는 건들 (v2.182.0 · 재고관리와 같은 창).
          ⚠자료를 아직 못 받았을 때도 이 자리는 살아 있어야 한다 — 그래서 위 `summaryQ.data`
            가지 **밖**에 둔다. lazy 라 누르기 전까지는 내려받지도 않는다. */}
      <Suspense fallback={null}>
        {drill && (
          <StockBreakdownModal
            itemCode={drill.itemCode}
            kind={drill.kind}
            onSalesClick={(id) => setDrillSalesId(id)}
            onClose={() => setDrill(null)}
          />
        )}
        {drillSalesId && (
          <SalesSummaryModal salesId={drillSalesId} onClose={() => setDrillSalesId(null)} />
        )}
      </Suspense>
    </div>
  );
}

function EmptyNote({ text }: { text: string }) {
  return <div className="text-center text-slate-400 text-sm py-8">{text}</div>;
}

function AlertDetailModal({
  title,
  color,
  count,
  onClose,
  goLabel,
  onGo,
  children,
}: {
  title: string;
  color: string;
  count?: string;
  onClose: () => void;
  goLabel: string;
  onGo: () => void;
  children: ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto py-10"
    >
      <div
        className="bg-white rounded shadow-xl w-[640px] max-w-[95vw] max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-slate-50 rounded-t">
          <div className="flex items-center gap-2 min-w-0">
            <span className={`w-2.5 h-2.5 rounded-full ${color} inline-block flex-shrink-0`} />
            <h2 className="ui-section-title truncate">{title}</h2>
            {count && <span className="text-sm text-slate-500 flex-shrink-0">{count}</span>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ui-close shrink-0 ml-3"
          >
            ×
          </button>
        </div>
        <div className="flex-1 overflow-auto p-5">{children}</div>
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 rounded-b flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary"
          >
            닫기
          </button>
          <button
            type="button"
            onClick={onGo}
            className="btn btn-primary"
          >
            {goLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function AlertItem({
  color,
  label,
  count,
  onClick,
  title,
}: {
  color: string;
  label: string;
  count: string;
  onClick?: () => void;
  title?: string;
}) {
  const inner = (
    <>
      <span className={`w-2 h-2 rounded-full ${color} inline-block`} />
      {label} <strong>{count}</strong>
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        title={title}
        className="btn btn-secondary btn-sm"
      >
        {inner}
        <span className="text-slate-500 text-xs">›</span>
      </button>
    );
  }
  return (
    <div className="flex items-center gap-2 px-2.5 py-1">
      {inner}
    </div>
  );
}

/**
 * 큰 숫자 카드 (v2.78.0 부터 누를 수 있다).
 *   이번달 매출·이익 → 구성 내역 팝업 (영업건 / 유지보수 / 비상벨)
 *   진행중 영업건     → 영업진행 메뉴로 이동
 *   발행대기          → 계산서 미발행 목록 팝업 (알림 줄과 같은 팝업 재사용)
 * ⭐금액이 null 인 권한(viewer)에서는 누를 것이 없으니 종전처럼 그냥 글씨로 둔다.
 */
function MetricCards({
  data,
  showCost,
  onCardClick,
}: {
  data: dashboardApi.DashboardMetrics;
  showCost: boolean;
  onCardClick: (key: 'monthly' | 'active' | 'pending') => void;
}) {
  const cards: {
    title: string; value: string; sub: string; cls: string; show: boolean;
    go?: 'monthly' | 'active' | 'pending';
    goHint?: string;
  }[] = [
    {
      title: '이번달 매출',
      value: data.monthlyRevenue === null ? '—' : fmtKrw(data.monthlyRevenue),
      sub: '발행+유지보수+비상벨',
      cls: 'text-emerald-600',
      show: true,
      go: data.monthlyParts ? 'monthly' : undefined,
      goHint: '클릭 → 영업건·유지보수·비상벨로 나눠 보기',
    },
    {
      title: '이번달 이익',
      value: data.monthlyProfit === null ? '—' : fmtKrw(data.monthlyProfit),
      sub: '매출 − 매입',
      cls: 'text-emerald-600',
      show: true,
      go: data.monthlyParts ? 'monthly' : undefined,
      goHint: '클릭 → 무엇에서 얼마가 남았는지 나눠 보기',
    },
    {
      title: '진행중 영업건',
      value: fmtCount(data.activeSales.count),
      sub:
        data.activeSales.totalAmount === null
          ? ''
          : fmtKrw(data.activeSales.totalAmount),
      cls: 'text-slate-600',
      show: true,
      go: 'active',
      goHint: '클릭 → 영업진행 목록으로 이동',
    },
    {
      title: '발행대기',
      value:
        data.pendingInvoice === null
          ? '—'
          : fmtCount(data.pendingInvoice.count),
      sub:
        data.pendingInvoice === null || data.pendingInvoice.totalAmount === null
          ? ''
          : fmtKrw(data.pendingInvoice.totalAmount),
      cls: 'text-amber-600',
      show: showCost && data.pendingInvoice !== null,
      go: 'pending',
      goHint: '클릭 → 계산서 미발행 건 보기',
    },
  ];
  const visible = cards.filter((c) => c.show);
  // 동적 lg:grid-cols-N — Tailwind JIT 가 안전하게 인식하도록 정적 클래스로 분기
  const lgCols = visible.length === 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3';
  return (
    <div className={`grid gap-3 md:gap-4 mb-6 grid-cols-1 sm:grid-cols-2 ${lgCols}`}>
      {visible.map((m) => {
        const inner = (
          <>
            <div className="text-xs text-slate-500">
              {m.title}
              {m.go && <span className="text-slate-300 ml-1">›</span>}
            </div>
            <div className="text-xl md:text-2xl font-bold mt-1 num">{m.value}</div>
            {m.sub && <div className={`text-xs ${m.cls} mt-1`}>{m.sub}</div>}
          </>
        );
        if (!m.go) {
          return (
            <div
              key={m.title}
              className="bg-white rounded shadow-sm border border-slate-200 p-4 md:p-5"
            >
              {inner}
            </div>
          );
        }
        return (
          <button
            key={m.title}
            type="button"
            onClick={() => onCardClick(m.go!)}
            title={m.goHint}
            className="bg-white rounded shadow-sm border border-slate-200 p-4 md:p-5 text-left hover:border-blue-300 hover:shadow transition-colors"
          >
            {inner}
          </button>
        );
      })}
    </div>
  );
}

function MonthlyChart({ data }: { data: dashboardApi.DashboardMonthlyComparison[] }) {
  const max = data.reduce(
    (m, d) => Math.max(m, d.current ?? 0, d.previous ?? 0, d.previous2 ?? 0),
    0,
  );
  const heightOf = (v: number | null): number => {
    if (!v || max === 0) return 0;
    return Math.max(2, (v / max) * 140);
  };
  const curYear = new Date().getFullYear();
  return (
    <div className="bg-white rounded shadow-sm border border-slate-200 p-5">
      <div className="text-sm font-semibold mb-3">
        월별 매출 추이 ({curYear} · {curYear - 1} · {curYear - 2})
      </div>
      <div className="flex items-end gap-1" style={{ height: 160 }}>
        {data.map((d) => (
          <div key={d.month} className="flex-1 flex flex-col items-center gap-1">
            <div className="w-full flex items-end gap-0.5" style={{ height: 140 }}>
              <div
                className="flex-1 bg-blue-500 rounded-t"
                style={{ height: `${heightOf(d.current)}px` }}
                title={`${curYear}년 ${d.month}월: ${fmtKrw(d.current)}`}
              />
              <div
                className="flex-1 bg-slate-400 rounded-t"
                style={{ height: `${heightOf(d.previous)}px` }}
                title={`${curYear - 1}년 ${d.month}월: ${fmtKrw(d.previous)}`}
              />
              <div
                className="flex-1 bg-slate-200 rounded-t"
                style={{ height: `${heightOf(d.previous2)}px` }}
                title={`${curYear - 2}년 ${d.month}월: ${fmtKrw(d.previous2)}`}
              />
            </div>
            <div className="text-[11px] text-slate-500">{d.month}월</div>
          </div>
        ))}
      </div>
      <div className="flex gap-3 text-xs mt-2">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 bg-blue-500 inline-block" />
          {curYear}
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 bg-slate-400 inline-block" />
          {curYear - 1}
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 bg-slate-200 inline-block" />
          {curYear - 2}
        </span>
      </div>
    </div>
  );
}

/**
 * ⏰ 딜등록 만료 임박 칸 (v2.142.0) — 연장 마감이 가까운 순. 줄을 누르면 그 딜의 상세(딜등록 요청 화면)로.
 * 표시 규칙(제안사·엔드유저·프로젝트 줄이기 · D-day 색)은 딜등록 화면과 같은 lib/deal-display 를 쓴다.
 */
const DEAL_PANEL_MAX = 8;
function DealExpiringPanel({
  data, scope, days, onOpen, onGoAll,
}: {
  data: dealApi.DealRow[];
  scope: 'all' | 'mine' | 'none';
  days: number;
  onOpen: (id: number) => void;
  onGoAll: () => void;
}) {
  const shown = data.slice(0, DEAL_PANEL_MAX);
  return (
    <div className="bg-white rounded shadow-sm border border-slate-200 p-5 mb-6">
      <div className="flex items-center gap-2 mb-3">
        <div className="text-sm font-semibold">
          ⏰ 딜등록 만료 임박
          <span className="ml-1 text-xs font-normal text-slate-500">(연장 마감 {days}일 이내 · {scope === 'all' ? '전체' : '내 담당'})</span>
        </div>
        <button type="button" onClick={onGoAll} className="ml-auto text-xs text-blue-700 hover:underline">딜등록 요청으로 이동 ›</button>
      </div>
      {data.length === 0 ? (
        <div className="text-center text-slate-400 text-sm py-6">{days}일 이내 연장 마감인 딜등록이 없습니다</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="data-table dense w-full text-xs">
            <thead>
              <tr>
                <th className="text-left w-[128px]">연장 마감</th>
                <th className="text-left w-[130px]">접수번호<br /><span className="font-normal text-slate-400">Project ID</span></th>
                <th className="text-left w-[18%] min-w-[130px]">제안사</th>
                <th className="text-left w-[20%] min-w-[140px]">엔드유저</th>
                <th className="text-left w-[36%] min-w-[200px]">프로젝트</th>
                {scope === 'all' ? <th className="text-left w-[64px]">담당자</th> : null}
              </tr>
            </thead>
            <tbody>
              {shown.map((d) => {
                const dd = ddayText(d);
                return (
                  <tr key={d.id} onClick={() => onOpen(d.id)} className="cursor-pointer hover:bg-orange-50/60" title="이 딜의 상세 열기">
                    <td className="text-left align-top whitespace-nowrap tabular-nums">
                      {d.deadlineDate}
                      {dd ? <span className={`ml-1 px-1.5 rounded text-xs font-semibold ${dd.cls}`}>{dd.text}</span> : null}
                    </td>
                    <td className="text-left align-top">
                      <div className="font-semibold text-slate-800 tabular-nums truncate max-w-[120px]" title={d.registrationNo}>{d.registrationNo || '-'}</div>
                      <div className="text-slate-500 tabular-nums truncate max-w-[120px]" title={d.projectId}>{d.projectId || '-'}</div>
                    </td>
                    <td className="text-left align-top max-w-0"><div className="truncate" title={d.tier2}>{dealCompanyLabel(d) || '-'}</div></td>
                    <td className="text-left align-top max-w-0"><div className="truncate" title={d.endUser}>{dealEndUser(d) || '-'}</div></td>
                    <td className="text-left align-top max-w-0"><div className="truncate" title={projectTitle(d.projectInfo)}>{projectTitle(d.projectInfo) || '-'}</div></td>
                    {scope === 'all' ? <td className="text-left align-top"><div className="truncate max-w-[60px]" title={d.ownerName}>{d.ownerName || '-'}</div></td> : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {data.length > shown.length ? (
            <div className="px-2 py-2 text-xs text-slate-500">
              외 {data.length - shown.length}건 — <button type="button" onClick={onGoAll} className="text-blue-700 hover:underline">전부 보기 ›</button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

function UpcomingPanel({ data }: { data: dashboardApi.DashboardUpcomingDelivery[] }) {
  return (
    <div className="bg-white rounded shadow-sm border border-slate-200 p-5">
      <div className="text-sm font-semibold mb-3">납품 임박 (D+14 이내)</div>
      {data.length === 0 ? (
        <div className="text-center text-slate-400 text-sm py-6">
          14일 이내 납품 예정 없음
        </div>
      ) : (
        <ul className="space-y-2 text-sm">
          {data.map((d) => (
            <li
              key={d.salesId}
              className="flex justify-between p-2 bg-amber-50 rounded"
            >
              <span className="min-w-0 flex-1">
                <strong>{d.salesId}</strong>{' '}
                <span className="text-slate-700">
                  {d.endUser ?? ''}
                  {d.projectName && ` · ${d.projectName}`}
                </span>
              </span>
              <span className="text-amber-700 whitespace-nowrap ml-2">
                {d.deliveryDate}
                <span className="ml-1 text-[11px]">(D{d.daysLeft >= 0 ? '-' : '+'}{Math.abs(d.daysLeft)})</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
